// TPOC integration: Send loan data to TPOC LoanModelConverter endpoint
import ssf from '@elliemae/em-ssf-guest';
import { loanChannelConfiguration } from '@frontend/pricing-search';
import { isEmpty } from 'lodash';
import { buildEpcLoanObject } from '../../data/lockSummary/actionCreators';
import {
  TPOC_EVENT_NAME,
  TPOC_SOURCE_APPLICATION_FORM,
} from '../constants/ActionConstants';
import Session, { ORIGIN_SOURCE } from './Session';
import { runFetchJson } from '../utils/runFetch';
import { getAppConfigValue } from '@elliemae/pui-app-sdk';
import Common from './Common';
import createFetch from '../utils/createFetch';

/**
 * TPOC (Third Party Origination Component) Services
 * Sends loan data to TPOC LoanModelConverter endpoint
 *
 * Architecture: POST to /ice-ppe-admin/v1/LoanModelConverter with complete loan model
 */

/**
 * Original loan information from Redux state
 * Includes all fields expected by LoanModelConverter endpoint
 */
export interface OriginalLoanInfo {
  id?: string;
  baseLoanAmount?: number;
  loanAmortizationTermMonths?: number;
  property?: any;
  applications?: Array<any>;
  cashFromToBorrowerAmount?: number;
  propertyAppraisedValueAmount?: number;
  loanProductData?: any;
  loanProgramName?: string;
  firstSubordinateLienAmount?: number;
  subjectPropertyGrossRentalIncomeAmount?: number;
  mortgageType?: string;
  subjectPropertyOccupancyPercent?: number;
  miAndFundingFeeFinancedAmount?: number;
  rateLock?: any;
  closingCost?: any;
  loCompensation?: any;
  vaLoanData?: any;
  hmda?: any;
  options?: number;
  fnmCommunitySecondsIndicator?: boolean;
  print2003Application?: string;
  propertyEstimatedValueAmount?: number;
  borrowerRequestedLoanAmount?: number;
  miscellaneous?: any;
  purchasePriceAmount?: string;
  loanAmortizationType?: string;
  ltv?: number;
  combinedLtv?: number;
  hcltvHtltv?: number;
  lockRequests?: Array<any>;
  lockDays?: Array<number>;
  lockUpdateRequest?: any;
  relockRequest?: any;
  scenarioTypeID?: number;
  lockedProgramID?: number;
  requestAction?: string;
  requestLockType?: string;
  requestLockStatus?: string;
  buydownType?: string;
  buydownContributor?: string;
  compensation?: any;
  customId?: string;
  deliveryType?: number;
  documentationLevel?: string;
  encompassIdentity?: string;
  eppsUserName?: string;
  facadeUserID?: string;
  loanType?: string;
  lockCancellationDate?: string;
  lockExpirationDate?: string;
  lockedStreetAddress?: string;
  lockRequestAdditionalFields?: Array<any>;
  productOptions?: Array<number>;
  productType?: Array<number>;
  specialProducts?: Array<number>;
  standardProducts?: Array<number>;
  userTierId?: number;
  wcpRequest?: any;
  borrowerFinancialHistory?: any;
  agencyApprovals?: number;
  // Additional fields from loan model that should be included
  applicationReceivedDate?: string;
  averageRepresentativeCreditScore?: string;
  cashOut?: number;
  channel?: string;
  commitmentType?: number;
  communityAffordableSecond?: boolean;
  condotel?: boolean;
  constructionRefinance?: boolean;
  dtiDebtOverride?: number;
  dtiIncomeOverride?: number;
  estimatedClosingCost?: number;
  feeAmount?: number;
  feeAmountFinanced?: number;
  feeAmountPaidinCash?: number;
  fhaCaseNumberDate?: string;
  financedAmount?: number;
  firstMortgageAmount?: number;
  giftForDownPayment?: number;
  hedged?: string;
  helocCreditLimit?: number;
  helocDrawnTotal?: number;
  helocInitialDraw?: number;
  includeClosingCost?: boolean;
  lenderFeeWaiver?: boolean;
  lienPosition?: string;
  loanAmountMaxLine?: number;
  loanChannel?: number;
  loanId?: number;
  loanOfficerId?: number;
  loanPurpose?: number;
  lockDate?: string;
  mtgIns?: number;
  noClosingCost?: boolean;
  noLoan?: boolean;
  non_warrantable_Condo?: boolean;
  originationDate?: string;
  otherHELOCDraws?: number;
  otherHELOCLines?: number;
  otherPayment?: number;
  prepaymentPenaltyTerm?: number;
  secondMortgageAmount?: number;
  sectionOfAct?: string;
  selfEmployedBorrower?: boolean;
  selfEmployedCoBorrower?: boolean;
  sellerConsessionForDownPayment?: number;
  target?: number;
  term?: number;
  totalMortgageAmount?: number;
  totSubClosedEndMort?: number;
  underwritingMethodID?: number;
  usdaGuarantee?: number;
  vaFirstTimeUse?: boolean;
  vaFundingFeeExempted?: boolean;
  wsChannel?: string;
}

export interface RateSelectorSummary {
  loan?: {
    rate?: number;
    price?: number;
    baseLoanAmount?: number;
    propertyValue?: number;
    ltv?: number;
    comments?: string;
  };
  lien?: {
    lockTerm?: number;
    programID?: number;
    programName?: string;
    investorName?: string;
    rateDataID?: number;
    priceTable?: any;
  };
  optionId?: string;
  originId?: string;
}

export interface RateStackData extends RateSelectorSummary {
  programID?: number;
  program?: string;
  rateDate?: string;
  rateDataID?: number;
  rate?: number;
  price?: number;
  priceVal?: number;
  comp?: number;
  compVal?: number;
  holdback?: number;
  pnI?: number;
  bRatio?: number | null;
  bonaFideDiscountPointsTest?: unknown;
  bonaFideDiscountPointsFactor?: number;
  margin?: number;
  apr?: number;
  basePrice?: number;
  target?: {
    rate?: number;
    price?: number;
    apr?: number;
    pnI?: number;
  };
}

export interface TPOCLoanPayload {
  agencyApprovals?: number;
  borrowerFinancialHistory?: any;
  borrowers?: Array<any>;
  buydownContributor?: string;
  buydownType?: string;
  compensation?: any;
  customId?: string;
  deliveryType?: number;
  documentationLevel?: string;
  encompassIdentity?: string;
  eppsUserName?: string;
  facadeUserID?: string;
  loanInformation: OriginalLoanInfo;
  loanType?: string;
  lockCancellationDate?: string;
  lockDays?: Array<number>;
  lockedProgramID?: number;
  lockedStreetAddress?: string;
  lockExpirationDate?: string;
  lockRequestAdditionalFields?: Array<any>;
  lockUpdateRequest?: any;
  productOptions?: Array<number>;
  productType?: Array<number>;
  property?: any;
  relockRequest?: any;
  requestAction?: string;
  requestLockStatus?: string;
  requestLockType?: string;
  scenarioTypeID?: number;
  sourceApplicationFormName?: string;
  sourceApplicationName?: string;
  specialProducts?: Array<number>;
  standardProducts?: Array<number>;
  userTierId?: number;
  wcpRequest?: any;
  // Rate stack data from pricing engine (allRatesRowData from Redux)
  rateStackData?: RateStackData;
  [key: string]: any;
}

/**
 * ENC V3 Model (LoanContract)
 * Response from /ice-ppe-admin/v1/LoanModelConverter endpoint
 * Represents the Encompass V3 model format for TPOC
 */
export interface ENCv3LoanModel {
  applicationReceivedDate?: string;
  cashOut?: number;
  channel?: string;
  commitmentType?: number;
  condotel?: boolean;
  creditScoreToUse?: string;
  dtiDebtOverride?: number;
  dtiIncomeOverride?: number;
  estimatedClosingCost?: number;
  feeAmount?: number;
  feeAmountFinanced?: number;
  feeAmountPaidinCash?: number;
  fhaCaseNumberDate?: string;
  financedAmount?: number;
  firstMortgageAmount?: number;
  fnmCommunitySecondsIndicator?: boolean;
  giftForDownPayment?: number;
  helocCreditLimit?: number;
  helocDrawnTotal?: number;
  helocInitialDraw?: number;
  lienPosition?: string;
  loanAmountMaxLine?: number;
  loanPurpose?: number;
  mtgIns?: number;
  noLoan?: boolean;
  non_warrantable_Condo?: boolean;
  originationDate?: string;
  otherHELOCDraws?: number;
  otherHELOCLines?: number;
  otherPayment?: number;
  print2003Application?: boolean;
  proposedMortgageInsuranceAmount?: number;
  rateLock?: any;
  secondMortgageAmount?: number;
  sectionOfAct?: string;
  selfEmployedBorrower?: boolean;
  selfEmployedCoBorrower?: boolean;
  sellerConsessionForDownPayment?: number;
  target?: number;
  term?: number;
  totalMortgageAmount?: number;
  totSubClosedEndMort?: number;
  underwritingMethodID?: number;
  usdaGuarantee?: number;
  vaFirstTimeUse?: boolean;
  wsChannel?: string;
  [key: string]: any; // Allow additional fields from conversion
}

/**
 * Build TPOC payload using the same loan data sent to the Rates (/loanQualifier) endpoint.
 * The LoanModelConverter API expects this exact same payload structure.
 * @param {RateStackData | null | undefined} rateStackData - Selected rate/program row from allRatesRowData in Redux
 * @param {Partial<TPOCLoanPayload>} loanData - Full loan data from Redux (state.EPPS.loanInfo) — same payload used for Rates call
 * @returns {TPOCLoanPayload} Payload ready to POST to /ice-ppe-admin/v1/LoanModelConverter
 */
export const buildPayloadForConversion = (
  rateStackData: RateStackData | null | undefined,
  loanData: Partial<TPOCLoanPayload>,
): TPOCLoanPayload => {
  // Build payload from loan data (same structure sent to Rates API)
  const payload: TPOCLoanPayload = {
    ...(loanData as TPOCLoanPayload),
    sourceApplicationFormName: TPOC_SOURCE_APPLICATION_FORM,
    // Include selected rate stack data from pricing results
    ...(rateStackData && { rateStackData }),
  };

  return payload;
};

/**
 * Convert EPPS loan model to ENC V3 format via LoanModelConverter endpoint
 * @param {TPOCLoanPayload} payload - EPPS loan payload to convert
 * @returns {Promise<ENCv3LoanModel>} Converted ENC V3 model
 */
export const convertLoanToENCv3 = async (
  payload: TPOCLoanPayload,
): Promise<ENCv3LoanModel> => {
  if (!payload?.loanInformation) {
    throw new Error('Invalid TPOC payload: missing loanInformation');
  }

  let convertedModel: ENCv3LoanModel;
  try {
    convertedModel = await runFetchJson(async () => {
      const widgetServerAPI = getAppConfigValue<string>(
        'serviceEndpoints.partnerApi',
      );

      const originId = await Common.getOriginId();

      const loanModelConverterResponseFilter = async (response: Response) => {
        if (response.ok) {
          return response;
        }
        const errorBody = await response
          .text()
          .catch(() => '(could not read body)');
        console.error(
          `LoanModelConverter ${response.status} error:`,
          errorBody,
        );
        throw new Error(`LoanModelConverter ${response.status}: ${errorBody}`);
      };

      return createFetch(
        `${widgetServerAPI}/loanModelConverter?OriginId=${originId}`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        },
        {},
        {},
        [loanModelConverterResponseFilter],
        true,
      );
    });
  } catch (error) {
    console.error('Failed to convert loan to ENC V3 model:', error);
    throw error;
  }

  return convertedModel as ENCv3LoanModel;
};

/**
 * Build TPOC payload using the same loan data sent to the Rates (/loanQualifier) endpoint.
 * The LoanModelConverter API expects this exact same payload structure.
 * @param {RateStackData | null | undefined} rateStackData - Selected rate/program row from allRatesRowData in Redux
 * @param {Partial<TPOCLoanPayload>} loanData - Full loan data from Redux (state.EPPS.loanInfo) — same payload used for Rates call
 * @returns {TPOCLoanPayload} Payload ready to POST to /ice-ppe-admin/v1/LoanModelConverter
 */
export const buildTPOCPayload = (
  payload: Partial<TPOCLoanPayload>,
  loanInfo: any,
  summaryDetails: any,
  originalLoanInfo: any,
): TPOCLoanPayload => {
  const getCustomFields = loanInfo?.lockRequestAdditionalFields || [];
  const loanData = loanInfo?.loanInformation || [];

  const pricingOriginSource = Session.get(ORIGIN_SOURCE) || '';
  const customFields: any[] = [];

  if (!isEmpty(getCustomFields)) {
    getCustomFields.map((item) => {
      return customFields.push({
        fieldId: item.fieldId,
        value: item.value,
        dataType: item.dataType,
      });
    });
  }

  const epcObject = buildEpcLoanObject({
    summaryDetails,
    pricingOriginSource,
    originalLoanInfo,
    loanData,
    customFields,
  }).loan;

  // Tweak payload from loan data to prevent validation errors for TPOC
  // Channel mapping
  payload.channel = loanChannelConfiguration[loanData.loanChannel!];
  if (loanData.loanChannel === 4) {
    payload.tpo = Object.assign(payload.tpo ?? {}, {
      underwritingDelegated: loanData.wsChannel === 'CorrespondentDelegated',
    });
    payload.correspondent = Object.assign(payload.correspondent ?? {}, {
      commitmentType: epcObject.correspondentCommitmentType,
      deliveryType: epcObject.correspondentDeliveryType,
    });
  }

  // Loan mapping
  payload.closingCost = {
    ...payload.closingCost,
    gfe2010: epcObject?.noClosingCostOption?.gfe2010,
  };
  payload.contacts = epcObject?.contacts;
  payload.gfe = epcObject?.gfe;
  payload.loanAmortizationTermMonths = epcObject?.loanAmortizationTermMonths;
  payload.loanAmortizationType = epcObject?.loanAmortizationType;
  payload.loanProductData = {
    ...payload.loanProductData,
    ...epcObject?.loanProductData,
  };
  payload.loanProgramName = epcObject?.loanProgramName;
  payload.miAndFundingFeeFinancedAmount =
    epcObject?.miAndFundingFeeFinancedAmount;
  payload.mipPaidInCashAmount = epcObject?.mipPaidInCashAmount;
  payload.mortgageType = epcObject?.mortgageType;
  payload.noClosingCostOption = epcObject?.noClosingCostOption;
  payload.requestedInterestRatePercent =
    epcObject?.requestedInterestRatePercent;
  payload.twelveMonthMortgageRentalHistoryIndicator =
    epcObject?.twelveMonthMortgageRentalHistoryIndicator;
  payload.unDiscountedRate = epcObject?.unDiscountedRate;

  // Rate Lock Mapping
  const rateLockData = epcObject.rateLock;
  payload.rateLock = {
    ...payload.rateLock,
    ...rateLockData,
  };
  // Transform from number string to description string
  if (loanData.loanChannel === 4) {
    payload.rateLock.commitmentType = epcObject?.correspondentCommitmentType;
    payload.rateLock.deliveryType = epcObject?.correspondentDeliveryType;
  }
  delete payload.rateLock.floatDateUpdatedOn;

  const internalFields = [
    'lienPosition',
    'loanPurpose',
    'firstMortgageAmount',
    'totalMortgageAmount',
    'financedAmount',
    'cashOut',
    'target',
    'condotel',
    'selfEmployedBorrower',
    'selfEmployedCoBorrower',
    'non_warrantable_Condo',
    'commitmentType',
    'wsChannel',
    'feeAmountPaidinCash',
    'feeAmountFinanced',
    'feeAmount',
  ] as const;
  for (const field of internalFields) {
    delete payload[field];
  }

  console.log('[TPOC] LoanModelConverter response:', payload);

  return payload as any;
};

/**
 * Send converted ENC V3 loan model to HA via SSF notifyEvent
 * @param {ENCv3LoanModel} convertedModel - ENC V3 model (converted loan data)
 * @returns {Promise<ENCv3LoanModel>} The sent model
 */
export const sendLoanToTPOC = async (
  convertedModel: ENCv3LoanModel,
): Promise<ENCv3LoanModel> => {
  if (!convertedModel) {
    throw new Error('Invalid ENC V3 model provided to sendLoanToTPOC');
  }

  const transaction = await ssf.getObject('transaction');
  if (!transaction) {
    console.error(
      '[TPOC] SSF transaction object unavailable — cannot send loan to HA',
    );
    throw new Error('Transaction object not available');
  }

  try {
    await transaction.notifyEvent({
      eventType: TPOC_EVENT_NAME,
      data: convertedModel,
    });
  } catch (error) {
    console.error('Failed to send TPOC payload to HA:', error);
    throw error;
  }

  return convertedModel;
};
